import apiClient from '../../utils/apiClient';
import { API_ENDPOINTS } from '../../config/apiEndpoints';
import { Role, RoleWithDetails, GroupedPermissions, Permission } from '../../types/role.types';

/**
 * Role Management API Service
 * Centralizes all role and permission related backend interactions using
 * the 100% dynamic API_ENDPOINTS registry and type-safe apiClient.
 */
export const roleApi = {
    // --- General Role Endpoints ---

    fetchRoles: async (): Promise<RoleWithDetails[]> => {
        return apiClient<RoleWithDetails[]>(API_ENDPOINTS.ROLES.BASE);
    },

    fetchPermissions: async (): Promise<GroupedPermissions> => {
        return apiClient<GroupedPermissions>(API_ENDPOINTS.ROLES.PERMISSIONS);
    },

    createRole: async (name: string, description: string): Promise<Role> => {
        return apiClient<Role>(API_ENDPOINTS.ROLES.BASE, {
            method: 'POST',
            data: { name, description },
        });
    },

    updateRolePermissions: async (roleId: string, permissionIds: string[]): Promise<void> => {
        return apiClient(API_ENDPOINTS.ROLES.UPDATE_PERMISSIONS(roleId), {
            method: 'PUT',
            data: { permissionIds },
        });
    },

    deleteRole: async (roleId: string): Promise<void> => {
        return apiClient(API_ENDPOINTS.ROLES.BY_ID(roleId), {
            method: 'DELETE',
        });
    },

    // --- Admin Forensic Endpoints (Used in RoleDetailsPage) ---

    getRoleByIdAdmin: async (roleId: string): Promise<Role> => {
        return apiClient<Role>(API_ENDPOINTS.ADMIN.ROLES.BY_ID(roleId));
    },

    getAllPermissionsAdmin: async (): Promise<Permission[]> => {
        return apiClient<Permission[]>(API_ENDPOINTS.ADMIN.ROLES.PERMISSIONS);
    },

    addPermissionToRole: async (roleId: string, permissionId: string, expectedVersion: number): Promise<void> => {
        return apiClient(API_ENDPOINTS.ADMIN.ROLES.ROLE_PERMISSION(roleId, permissionId), {
            method: 'POST',
            data: { expectedVersion },
        });
    },

    removePermissionFromRole: async (roleId: string, permissionId: string, expectedVersion: number): Promise<void> => {
        return apiClient(API_ENDPOINTS.ADMIN.ROLES.ROLE_PERMISSION(roleId, permissionId), {
            method: 'DELETE',
            data: { expectedVersion },
        });
    },
};
